import { Grid, Typography, styled } from "@mui/material";

import RefCode from "../../../../images/refcode.png";
import Globe from "../../../../images/globe.png";
import Location from "../../../../images/city.png";
import Profession from "../../../../images/job.png";
import Education from "../../../../images/edu.png";
import CivilStatus from "../../../../images/status.png";
import Ethnicity from "../../../../images/ethnicity.png";
import Religion from "../../../../images/religion.png";
import Age from "../../../../images/age.png";
import Height from "../../../../images/height.png";
import Caste from "../../../../images/caste.png";
import ColumnEndPlaceholderImg from "../../../../images/column-end-placeholder2.png";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash";
import "./style.css";

const RowImage = styled("img")({
  height: "3mm",
});

const INNER_PADDING_THRESOLD = 4;
// const PROFESSION_LINE_BREAK_AT = 17;

// const isContainUnicodeChars = (input) => {
//   // eslint-disable-next-line no-control-regex
//   const regex = new RegExp(/[^\u0000-\u00ff]/);
//   return regex.test(input);
// };

const LocationSeperator = ({ mainSeperator, regionSeperator }) => {
  const [rowHeight, setRowHeight] = useState(0);
  const boxContainerRef = useRef(null);

  useEffect(() => {
    if (boxContainerRef.current === null) {
      return;
    }
    const boxContainer = boxContainerRef.current;
    setRowHeight(boxContainer.offsetHeight ?? 0);
  }, [boxContainerRef]);

  if (rowHeight === 0) {
    return (
      <Grid container direction="row" ref={boxContainerRef}>
        <AdvertismentView advertisment={undefined} />
      </Grid>
    );
  }
  return (
    <Grid
      container
      direction="column"
      border={0.25}
      borderRadius={1}
      height={rowHeight + INNER_PADDING_THRESOLD}
    >
      {mainSeperator !== undefined && (
        <Grid
          item
          container
          direction="column"
          bgcolor="black"
          color="white"
          paddingY={1}
        >
          <Typography variant="body1" textAlign="center">
            {mainSeperator.locationNameEN}
          </Typography>
          <Typography variant="body1" textAlign="center">
            {mainSeperator.locationNameSI}
          </Typography>
        </Grid>
      )}
      <Grid xs item container direction="column" justifyContent="center">
        <Typography variant="body1" textAlign="center">
          {regionSeperator.locationNameEN}
        </Typography>
        <Typography variant="body1" textAlign="center">
          {regionSeperator.locationNameSI}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ColumnEndPlaceholder = () => {
  const [rowHeight, setRowHeight] = useState(0);
  const boxContainerRef = useRef(null);

  useEffect(() => {
    if (boxContainerRef.current === null) {
      return;
    }
    const boxContainer = boxContainerRef.current;
    setRowHeight(boxContainer.clientHeight ?? 0);
  }, [boxContainerRef]);

  if (rowHeight === 0) {
    return (
      <Grid container direction="row" ref={boxContainerRef}>
        <AdvertismentView advertisment={undefined} />
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="column"
      border={0.25}
      borderRadius={1}
      height={rowHeight + INNER_PADDING_THRESOLD}
      alignItems="center"
      justifyContent="center"
    >
      <Grid xs item>
        <img
          src={ColumnEndPlaceholderImg}
          alt="column-end-placeholder"
          height={rowHeight - 5}
        />
      </Grid>
    </Grid>
  );
};

const HeaderInfoRow = forwardRef(({ image, label, visibility, href }, ref) => {
  const hasLink = Boolean(href);
  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      columnGap={0.5}
      visibility={visibility ? "visible" : "hidden"}
      ref={ref}
    >
      <RowImage src={image} />
      {hasLink ? (
        <a href={href}>
          <Typography
            variant="body2"
            fontWeight={600}
            fontSize={12}
            lineHeight="normal"
            className="grid-item-header-label"
          >
            {label}
          </Typography>
        </a>
      ) : (
        <Typography
          variant="body2"
          fontWeight={600}
          fontSize={12}
          lineHeight="normal"
          className="grid-item-header-label"
        >
          {label}
        </Typography>
      )}
    </Grid>
  );
});

const ContentRowSection = {
  LEFT: "LEFT",
  RIGHT: "RIGHT",
};

const ContentRow = ({
  field,
  image,
  label,
  section,
  noWrap = true,
  alignItems = "center",
}) => {
  // const hasUnicodeChars = isContainUnicodeChars(label);
  const labelRef = useRef(null);

  useEffect(() => {
    const labelElement = labelRef.current;
    if (
      field === "profession" &&
      typeof label === "string" &&
      labelElement instanceof HTMLElement
    ) {
      const labelTokens = label.split(" ");
      if (labelTokens.length < 3) {
        labelElement.style.wordBreak = "normal";
      } else {
        labelElement.style.wordBreak = "break-word";
      }
    }
  }, [field, label]);

  return (
    <Grid
      container
      direction="row"
      alignItems={alignItems}
      columnGap={0.75}
      flexWrap="nowrap"
      visibility={isEmpty(label) ? "hidden" : "visible"}
    >
      <RowImage src={image} />
      <Typography
        ref={labelRef}
        noWrap={noWrap}
        variant="body2"
        fontWeight={500}
        fontSize={12}
        lineHeight="initial"
        textOverflow="ellipsis"
        overflow="hidden"
        className="grid-item-info-label"
        data-ad-field-label={field}
      >
        {label || "N/A"}
      </Typography>
    </Grid>
  );
};

const PaperGridItem = ({ advertisment }) => {
  const isColumnEndPlaceholder = Boolean(
    advertisment && advertisment.columnEndPlaceholder
  );
  if (isColumnEndPlaceholder) {
    return <ColumnEndPlaceholder />;
  }

  const isLocationSeparator = Boolean(
    advertisment && advertisment.isLocationSeparator
  );
  if (!isLocationSeparator) {
    return <AdvertismentView advertisment={advertisment} />;
  }

  const { mainHeading, locationHeading } = advertisment;

  return (
    <LocationSeperator
      mainSeperator={mainHeading}
      regionSeperator={locationHeading}
    />
  );
};

const AdvertismentView = ({ advertisment }) => {
  const onlySkeleton = advertisment === undefined;
  const refCode = onlySkeleton ? "refCode" : advertisment.refCode ?? "";
  const postId = onlySkeleton ? "postId" : advertisment.postId;
  const resCity = onlySkeleton ? "resCity" : advertisment.resCity ?? "";
  const civilStatus = onlySkeleton ? "civilStatus" : advertisment.status ?? "";
  const education = onlySkeleton ? "education" : advertisment.education ?? "";
  const age = onlySkeleton ? "age" : advertisment.age ?? "";
  const heightLbl = onlySkeleton
    ? "height"
    : `${advertisment.height.replaceAll('"', "")}"` ?? "";
  const ethnicity = onlySkeleton ? "ethnicity" : advertisment.ethnicity ?? "";
  const religion = onlySkeleton ? "religion" : advertisment.religion ?? "";
  const caste = onlySkeleton ? "caste" : advertisment.caste ?? "";

  const postHeaderRowRef = useRef(null);
  const [headerRowHeight, setHeaderRowHeight] = useState(0);

  useEffect(() => {
    if (postHeaderRowRef.current !== null) {
      setHeaderRowHeight(postHeaderRowRef.current.clientHeight);
    }
  }, [postHeaderRowRef]);

  const computedProfession = useMemo(() => {
    const profession = (advertisment?.profession ?? "").trim();
    return profession;
  }, [advertisment]);

  return (
    <Grid
      direction="column"
      bgcolor="#e5e5e5"
      width={1}
      borderRadius={1}
      sx={{ opacity: onlySkeleton ? 0 : 1 }}
    >
      <Grid
        container
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        paddingX={1}
        bgcolor="#acacac"
        height={onlySkeleton ? "auto" : headerRowHeight * 2 + 4}
        sx={{ borderTopRightRadius: 2, borderTopLeftRadius: 2 }}
      >
        {(headerRowHeight === 0 || !isEmpty(refCode)) && (
          <HeaderInfoRow
            image={RefCode}
            label={`RefCode: ${refCode}`}
            visibility={true}
          />
        )}
        <HeaderInfoRow
          image={Globe}
          label={`Post Id: ${postId ?? ""}`}
          href={`https://www.poruwa.lk/post?id=${postId}`}
          visibility={!isEmpty(postId)}
          ref={postHeaderRowRef}
        />
      </Grid>
      <Grid container direction="row" padding={1}>
        <Grid item xs={7}>
          <ContentRow image={Location} label={resCity} />
          <ContentRow image={CivilStatus} label={civilStatus} />
          <ContentRow
            section={ContentRowSection.LEFT}
            image={Education}
            label={education}
          />
          <ContentRow
            field="profession"
            section={ContentRowSection.LEFT}
            image={Profession}
            label={computedProfession}
            noWrap={false}
            alignItems="start"
          />
        </Grid>
        <Grid item xs={5} style={{ paddingLeft: 8 }}>
          <ContentRow image={Age} label={`${age} years`} />
          <ContentRow image={Height} label={heightLbl} />
          <ContentRow
            section={ContentRowSection.RIGHT}
            image={Ethnicity}
            label={ethnicity}
          />
          <ContentRow image={Religion} label={religion} />
          <ContentRow
            image={Caste}
            label={caste}
            section={ContentRowSection.RIGHT}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PaperGridItem;
